import i18n from '@/utils/i18n';
import { CountryInput } from '@/views/Events/EventOrder/PublicEventOrderForm/components/CountryInput';
import {
  FieldLabel,
  TextField,
} from '@/views/Order/components/details/DetailFormParts';

import type { AddressField, AddressFormValue } from './types';

interface AddressFormProps {
  value: AddressFormValue;
  errors: Partial<Record<string, string>>;
  disabled?: boolean;
  isFieldSupported: (field: AddressField) => boolean;
  isFieldRequired: (field: AddressField) => boolean;
  onFieldChange: (field: AddressField, value: string) => void;
}

interface ReadOnlyAddressFormProps {
  value: AddressFormValue;
  disabled: true;
}

export function AddressForm({
  value,
  ...rest
}: AddressFormProps | ReadOnlyAddressFormProps) {
  const isEditable = 'onFieldChange' in rest;
  const disabled = isEditable ? rest.disabled : true;
  const errors: AddressFormProps['errors'] = isEditable ? rest.errors : {};
  const isFieldSupported: AddressFormProps['isFieldSupported'] = isEditable
    ? rest.isFieldSupported
    : () => true;
  const isFieldRequired: AddressFormProps['isFieldRequired'] = isEditable
    ? rest.isFieldRequired
    : () => false;
  const onFieldChange: AddressFormProps['onFieldChange'] = isEditable
    ? rest.onFieldChange
    : () => {};

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      <TextField
        fieldKey="address.name"
        label={i18n.t('Common:name')}
        value={value.name}
        onChange={(next) => onFieldChange('name', next)}
        required={isFieldRequired('name')}
        hidden={!isFieldSupported('name')}
        disabled={disabled}
        error={errors['address.name']}
      />
      <TextField
        fieldKey="address.company"
        label={i18n.t('Common:addressCompanyName')}
        value={value.company}
        onChange={(next) => onFieldChange('company', next)}
        required={isFieldRequired('company')}
        hidden={!isFieldSupported('company')}
        disabled={disabled}
        error={errors['address.company']}
      />
      <div className="tw:grid tw:grid-cols-2 tw:gap-4">
        <TextField
          fieldKey="address.phone_number"
          label={i18n.t('Common:phonenumber')}
          value={value.phone_number}
          onChange={(next) => onFieldChange('phone_number', next)}
          required={isFieldRequired('phone_number')}
          hidden={!isFieldSupported('phone_number')}
          disabled={disabled}
          error={errors['address.phone_number']}
        />
        <TextField
          fieldKey="address.email"
          label={i18n.t('Common:email')}
          value={value.email}
          onChange={(next) => onFieldChange('email', next)}
          required={isFieldRequired('email')}
          hidden={!isFieldSupported('email')}
          disabled={disabled}
          error={errors['address.email']}
        />
      </div>
      <TextField
        fieldKey="address.streetname"
        label={i18n.t('EventOrder:addressStreet')}
        value={value.streetname}
        onChange={(next) => onFieldChange('streetname', next)}
        required={isFieldRequired('streetname')}
        hidden={!isFieldSupported('streetname')}
        disabled={disabled}
        error={errors['address.streetname']}
      />
      <TextField
        fieldKey="address.additional_info"
        label={i18n.t('Common:streetExtra')}
        value={value.additional_info}
        onChange={(next) => onFieldChange('additional_info', next)}
        required={isFieldRequired('additional_info')}
        hidden={!isFieldSupported('additional_info')}
        disabled={disabled}
        error={errors['address.additional_info']}
      />
      <div className="tw:grid tw:grid-cols-2 tw:gap-4">
        <TextField
          fieldKey="address.zip"
          label={i18n.t('Common:zipCode')}
          value={value.zip}
          onChange={(next) => onFieldChange('zip', next)}
          required={isFieldRequired('zip')}
          hidden={!isFieldSupported('zip')}
          disabled={disabled}
          error={errors['address.zip']}
        />
        <TextField
          fieldKey="address.city"
          label={i18n.t('Common:city')}
          value={value.city}
          onChange={(next) => onFieldChange('city', next)}
          required={isFieldRequired('city')}
          hidden={!isFieldSupported('city')}
          disabled={disabled}
          error={errors['address.city']}
        />
      </div>
      {isFieldSupported('country') && (
        <div
          className="tw:flex tw:flex-col tw:gap-1.5"
          data-field="address.country"
        >
          <FieldLabel
            label={i18n.t('Common:country')}
            required={isFieldRequired('country')}
          />
          <CountryInput
            triggerClassName="tw:min-w-[200px] tw:self-start"
            onChange={(next) => {
              if (!next) return;
              onFieldChange('country', next);
            }}
            value={value.country}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}
